import {
  BaseUserProfile,
  Equipment,
  Gym,
  GymEnvironment,
  GymTypes,
  HOME_ACCOUNTID,
  IEnabledFeatures,
  IOrganisationConfig,
  IProductMetadata,
  Organisation,
} from 'fitbeat.models';
import { PaymentService } from 'fitbeat.payment-service';
import Stripe from 'stripe';
import { UserProfile } from '../../entities';
import { DbGymRepository, DbOrganisationRepository } from '../../persistence';
import { UserProfileService } from './UserProfileService';

export class EnabledFeaturesHydrationService {
  public static getGymOrganisation = (gym: Gym): IOrganisationConfig =>
    DbOrganisationRepository.get(gym.organisation);

  public constructor(
    private paymentService: PaymentService,
    private userProfileService: UserProfileService,
    private gymEnvironment: GymEnvironment,
  ) {}

  public build = async (userProfile: UserProfile) => {
    const allowedGymsForUser = await this.getAllAllowedGymForUser(userProfile);
    const userGym = allowedGymsForUser.find(
      (gym: Gym) => gym.name === userProfile.gymName,
    );
    const userOrganisation: IOrganisationConfig | undefined = userGym
      && EnabledFeaturesHydrationService.getGymOrganisation(userGym);

    const enabledFeatures: IEnabledFeatures = {
      organisationConfig: userOrganisation,
      workout: {
        homeGym: this.selectUserHomeGym(userProfile, allowedGymsForUser),
        allowedStudioGyms: [
          ...this.selectStudioGyms(allowedGymsForUser),
          ...this.selectOfficeScheduleGyms(allowedGymsForUser, userOrganisation?.name),
        ],
        home: {
          allowedEquipment: [Equipment.Bodyweight],
        },
      },
    };

    await this.applyEnabledEquipmentFeatures(userProfile, enabledFeatures);

    return enabledFeatures;
  };

  private getAllAllowedGymForUser = async (
    userProfile: UserProfile,
  ): Promise<Gym[]> => {
    if (!userProfile.currentPlanId) {
      return DbGymRepository.getGymsFor(
        userProfile,
        this.gymEnvironment,
        false,
      );
    }
    const canAccessAllStudioGymsInOrg = await this.userCanAccessAllStudioGymsInOrg(
      userProfile,
    );
    return DbGymRepository.getGymsFor(
      userProfile,
      this.gymEnvironment,
      canAccessAllStudioGymsInOrg,
    );
  };

  private async userCanAccessAllStudioGymsInOrg(
    user: UserProfile,
  ): Promise<boolean> {
    const plan = await this.paymentService.getOnceOffPriceWithProductMetaDataOrNull(
      user.currentPlanId,
    );
    const productOnPlan = plan?.product as Stripe.Product | null;
    const canAccessAllStudioGymsInOrg = productOnPlan?.metadata?.accessAllStudiosInOrg === 'true';
    return canAccessAllStudioGymsInOrg;
  }

  private applyEnabledEquipmentFeatures = async (
    userProfile: UserProfile,
    enabledFeatures: IEnabledFeatures,
  ) => {
    const allPrices = this.paymentService.getCachedPrices();

    const getEquipmentFromProductMetadata = async (user: BaseUserProfile) => {
      const price = allPrices.find(
        (pr: any) => pr.priceId === user.currentPlanId,
      );
      if (!price) {
        throw new Error(`no plan found with id: ${user.currentPlanId}`);
      }
      const { product } = price;
      return this.paymentService.getProductMetadataFromProduct(
        product as Stripe.Product,
      );
    };

    const addEquipmentFromProductMetadata = (
      productMetadata: IProductMetadata,
    ) => {
      if (productMetadata.equipment) {
        enabledFeatures.workout.home.allowedEquipment.push(
          ...productMetadata.equipment,
        );
      }
    };

    const applyEquipment = async (user: BaseUserProfile) => {
      enabledFeatures.workout.home.allowedEquipment = [Equipment.Bodyweight];
      if (user.currentPlanId) {
        const productMetadata = await getEquipmentFromProductMetadata(user);
        addEquipmentFromProductMetadata(productMetadata);
      }
    };

    if (userProfile.planOwnerUserProfileId) {
      const planOwnerUserProfile = await this.userProfileService.getUserProfile(
        userProfile.planOwnerUserProfileId,
      );
      if (!planOwnerUserProfile) {
        throw new Error(
          `plan owner not found: ${userProfile.planOwnerUserProfileId}`,
        );
      }
      await applyEquipment(planOwnerUserProfile);
    } else {
      await applyEquipment(userProfile);
    }
  };

  private selectUserHomeGym = (
    userProfile: BaseUserProfile,
    gymLocationList: Gym[],
  ) => {
    let homeGym = gymLocationList.find(
      (g) => g.name === userProfile.gymName && g.type === GymTypes.Home,
    );

    if (!homeGym) {
      homeGym = gymLocationList.find((g) => g.accountId === HOME_ACCOUNTID);
    }

    if (!homeGym) {
      throw new Error(`no home gym found for user: ${userProfile.uid}`);
    }

    return homeGym;
  };

  private selectOfficeScheduleGyms(gyms: Gym[], organisation: Organisation | undefined): Gym[] {
    return gyms
      .filter((gym) => gym.organisation === organisation)
      .filter(Gym.allowsOfficeScheduleStudioBookings);
  }

  private selectStudioGyms = (gyms: Gym[]) =>
    gyms.filter((gym: Gym) => gym.type === GymTypes.Indoor);
}
